import React from "react";
import OnboardingLayout from "./OnboardingLayout";
import { HiArrowRight } from "react-icons/hi";
import { HiOutlineSparkles } from "react-icons/hi2";

/** Step in the onboarding flow where users specify their work areas and assets. */
const WORK_AREAS = [
  "Trading",
  "Operations",
  "Asset Management",
  "Market Analysis",
  "Risk",
  "Other",
];

const ASSETS = [
  "Hydro",
  "Wind",
  "Solar",
  "Battery",
  "Thermal",
  "Load",
];

// Props for the StepNeeds component.
interface StepNeedsProps {
  selected?: string[];
  onSelect?: (value: string[]) => void;
  onContinue?: () => void;
  onBack?: () => void;
  hasExistingPersonas?: boolean;
  onExit?: () => void;
}

// StepNeeds component definition.
function StepNeeds({
  selected,
  onSelect,
  onContinue,
  onBack,
  hasExistingPersonas,
  onExit,
}: StepNeedsProps) {
  const activeValues = selected ?? [];

  // Toggle selection of an option.
  const toggleValue = (label: string) => {
    if (activeValues.includes(label)) {
      onSelect?.(activeValues.filter((item) => item !== label));
      return;
    }
    onSelect?.([...activeValues, label]);
  };

  // Render the component UI.
  return (
    <OnboardingLayout showProgress progressValue={0.2} hasExistingPersonas={hasExistingPersonas} onExit={onExit}>
      {/* Header and instructions */}
      <div className="px-8 pb-10 pt-6">
        <div className="flex items-start gap-4">
          <div className="rounded-3xl bg-[rgba(162,187,191,0.1)] p-2">
            <HiOutlineSparkles className="text-eai-primary-500" size={24} />
          </div>
          <div>
            <p className="text-base font-semibold text-eai-dark">
              Tell us about your role.
            </p>
            <p className="text-sm text-gray-500">
              Select all that apply.
            </p>
          </div>
        </div>

        {/* Work Areas Section */}
        <div className="mt-8">
          <p className="text-sm font-semibold text-eai-dark uppercase tracking-wide">
            WHAT DO YOU DO?
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {WORK_AREAS.map((label) => {
              const isActive = activeValues.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleValue(label)}
                  className={`rounded-xl border px-4 py-3 text-left text-base font-medium transition ${
                    isActive
                      ? "border-eai-primary-500 bg-[rgba(162,187,191,0.1)]"
                      : "border-eai-bg-lt2 hover:border-eai-primary-500"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Assets Section */}
        <div className="mt-8">
          <p className="text-sm font-semibold text-eai-dark uppercase tracking-wide">
            WHAT'S IMPORTANT
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {ASSETS.map((label) => {
              const isActive = activeValues.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleValue(label)}
                  className={`rounded-xl border px-4 py-3 text-left text-base font-medium transition ${
                    isActive
                      ? "border-eai-primary-500 bg-[rgba(162,187,191,0.1)]"
                      : "border-eai-bg-lt2 hover:border-eai-primary-500"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-eai-bg-lt2 px-4 py-2 text-sm text-eai-dark hover:border-eai-primary"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={activeValues.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-eai-primary-accent px-6 py-2 text-sm font-medium text-white transition enabled:hover:opacity-90 disabled:opacity-50"
          >
            Continue
            <HiArrowRight />
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default StepNeeds;
